import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import { Logger } from '../../../composables/useGtmLogRequest'
import { langFallback$Fetch } from '../../../lang/utils/langfetch'
import { SearchFilters } from '../schemas'
import {
  SearchNonPriceResultsResponse,
  SearchPriceResultsResponse,
  SearchResults,
} from './types'

const NON_PRICE_SEARCH_ENDPOINT = '/search2/facets'
const PRICE_SEARCH_ENDPOINT = '/search2/facets'

export class SearchCountsService {
  async fetchPriceSearchCounts(
    langCode: string,
    searchFilters: SearchFilters,
    logger: Logger,
  ): Promise<SearchResults> {
    const params = this.getQueryString(searchFilters)
    const url = `${PRICE_SEARCH_ENDPOINT}/?${params}`
    const gtmLogRequest = useGtmLogRequest(
      'SearchCountsService-fetchPriceSearchCounts',
      logger,
    )
    gtmLogRequest.markStart()
    const response = await langFallback$Fetch<SearchPriceResultsResponse>(
      langCode,
      url,
    )
    gtmLogRequest.markEnd()
    return this.formatPriceResults(response)
  }

  async fetchNonPriceSearchCounts(
    langCode: string,
    searchFilters?: SearchFilters,
    logger?: Logger,
  ): Promise<SearchResults> {
    const params = searchFilters ? `&${this.getQueryString(searchFilters)}` : ''
    const url = `${NON_PRICE_SEARCH_ENDPOINT}/?${params}&seo=none&sort=none&counts=1`
    const gtmLogRequest = useGtmLogRequest(
      'SearchCountsService-fetchNonPriceSearchCounts',
      logger,
    )
    gtmLogRequest.markStart()
    const response = await langFallback$Fetch<SearchNonPriceResultsResponse>(
      langCode,
      url,
    )
    gtmLogRequest.markEnd()
    return this.formatNonPriceResults(response)
  }

  private getQueryString(searchFilters: SearchFilters): string {
    const newFilters = {
      ...searchFilters,
      limit: 0,
    }
    return this.filtersToParams(newFilters).toString() + '&counts=1'
  }

  private filtersToParams(filters: SearchFilters): URLSearchParams {
    const params = new URLSearchParams()
    for (const param of parseSearchFiltersToUrl(filters)) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      // remove bounds if path
      if (params.get('path')) {
        params.delete('bounds')
      } else {
        // only full bookable if bounds and no path
        params.append('bookable', '1')
      }
    }
    return params
  }

  private formatNonPriceResults(
    results: SearchNonPriceResultsResponse,
  ): SearchResults {
    return {
      bookableCount: results.bookable_count,
      categoriesCounts: results.facet_category,
      hierarchyCounts: this.getHierarchiesFromSearchResults(results),
      keywordsCounts: results.facet_keywords,
    }
  }

  private getHierarchiesFromSearchResults(
    results?: SearchNonPriceResultsResponse,
  ) {
    const hierarchies = {}
    if (!results?.facet_hierarchy) {
      return hierarchies
    }
    for (const [key, value] of Object.entries(results.facet_hierarchy)) {
      hierarchies[key] = value
    }
    return hierarchies
  }

  private formatPriceResults(
    results: SearchPriceResultsResponse,
  ): SearchResults {
    return {
      bookableCount: results.facet_all,
      categoriesCounts: results.facet_category,
      hierarchyCounts: results.facet_hierarchy,
      keywordsCounts: results.facet_keywords,
      ...results,
    }
  }
}

export function getSearchCountService(): SearchCountsService {
  return new SearchCountsService()
}
